import type { PreloadedQuery } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteResource } from '@atlassian/react-resource-router';
import GSNQuery, {
	type GSNQueryPreloaderQuery,
} from '@jira/nav-platform-integration__sidebar/src/__generated__/GSNQueryPreloaderQuery.graphql.ts';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { getSideNavRelayVariables } from './getSideNavRelayVariables.tsx';

// Nullable on purpose. `createRelayResource` is cast to a non-null
// `RouteResource`, on the stated assumption that "consumers of
// `useRelayResource` already handle a `null` queryReference" — but `skip`
// (below) makes `getData` return `null` *without* suspending, and
// `useSuspenseResource` types `data` as non-null, so that assumption does not
// extend to suspense consumers. Widening here is what makes the skipped case
// visible to the compiler instead of surfacing as a `null` query reference
// inside a component that requires a real one. Safe because `RouteResource<T>`
// uses `T` only in `getData`'s return position, so it is covariant.
export const navPlatformSidebarResource: RouteResource<PreloadedQuery<
	GSNQueryPreloaderQuery,
	Record<string, unknown>
> | null> = createRelayResource<GSNQueryPreloaderQuery>({
	type: 'RESOURCE_TYPE_SIDEBAR_NAV_PLATFORM',
	// Anonymous users must not trigger this GSN GraphQL query. Use the
	// resource-level `skip` predicate so `loadQuery` is never invoked for them —
	// `getQuery` will not run when `skip` returns `true`.
	//
	// The same predicate also suppresses the query under the Nav Platform
	// code-path, where the side nav's data is already on the app-chrome query
	// (`useJiraAppChromeQuery` spreads `...GSNForContentSidebarEntryPoint` with
	// identical variables) and `GlobalSideNavigationSidebarSelector` reads it from
	// there. Loading this resource as well would fetch the same data twice
	// (NAVX-5414).
	//
	// Deliberately gated *here* rather than at each declaration site: this resource
	// is declared by both `getNavigationResources` and
	// `getCustomerServiceL1NavigationResources`, and gating per-site means a new
	// route family can reintroduce the duplicate fetch just by declaring it. One
	// choke point cannot be bypassed that way.
	skip: (_, { tenantContext: { isAnonymous } }) => {
		return isAnonymous || fg('navx-global-top-nav-jira');
	},
	getQuery: (routerContext, { tenantContext }) => {
		const fetchPolicy = 'store-or-network' as const;
		return {
			parameters: GSNQuery,
			// Shared with `appChromeResource`, which needs the same values as root
			// operation variables for `...GSNForContentSidebarEntryPoint`.
			variables: getSideNavRelayVariables(routerContext, tenantContext),
			options: { fetchPolicy },
		};
	},
	isCritical: true,
});
